
//Packages
import React, { useState, useEffect } from 'react';
import { Typography, Button, Box, Stack, useMediaQuery, Modal, Checkbox, TextField, Alert } from '@mui/material';
import { DataGrid, gridClasses } from '@mui/x-data-grid'
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';

// Local
import { colorTheme } from '../../constants';
import formatName from '../utilities/formatName';
import formatPhoneNumber from '../utilities/formatPhoneNumber';
import ExamTokenRequestModal from '../modals/ExamTokenRequestModal';
import getReservedTimeSlotsForUsers from '../helpers/getReservedTimeSlotsForUsers';
import formatTime from '../utilities/fromatTime';
import saveUserInfo from '../helpers/saveUserInfo';
import { getUsers } from '../helpers/requestUsers';
import sendWritingScoreReportEmail from '../helpers/sendWritingScoreReportEmail';
import { useLazyGetExamComponentQuery, usePostUserMutation } from '../../api';
import formatTimeZoneDateString from '../utilities/formatTimeZoneDate';
import { Paid } from '@mui/icons-material';
import { useSystemMessage } from '../../providers/SystemMessageContext';
import LiveAnswersModal from '../modals/LiveAnswersModal';

const SingleUserTile = ({ user_data, modalView}) => {
    /*
        parameters:
            user_data: dictionary with one user's information
                {
                    'id': int,
                    'username': str,
                    'first_name': str,
                    'last_name': str,
                    'phone_number': str,
                    'email': str,
                    'exam_tokens': int,
                    'attempts_remaining': int,
                    'writing_passed': bool,
                }
            modalView: boolean, changes rendering for if inside modal
        
        returns:
            SingleUserTile copmponent which displays the users given in the rows parameter inside an MUI DataGrid Component
    */

    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // DEFINITIONS
    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    const isMobile = useMediaQuery("(max-width:600px)");
    const theme = colorTheme.light;
    const { showMessage } = useSystemMessage();
    const [userData, setUserData] = useState(null);
    
    const { run: runTopScores, data:topScores, loading: loadingTopScores } = useLazyGetExamComponentQuery(null, null, userData ? true : false, userData ? userData.id : null);
    const { run: runAllExams, data: allExams, loading: loadingAllExams } = useLazyGetExamComponentQuery(null, null, null, userData ? userData.id : null);
    const { submit:triggerUserMutation, data:userMutation, error:userMutationError, isMutating: isPostingTokens } = usePostUserMutation();
    const [gridRows, setGridRows] = useState([]);
    const [maxRows, setMaxRows] = useState([]);
    const [userNotes, setUserNotes] = useState('');

    useEffect(() => {
        if (!userData) return;
        setUserNotes(userData.notes ? userData.notes : '');
    }, [userData?.id])

    useEffect(() => {
        if (userMutation) {
            showMessage(userMutation.message, userMutation.success ? "success" : "error");
        }
    }, [userMutation, userMutationError]);

    const handleUpdateNotes = async () => {
        await triggerUserMutation({
            "update_notes": {
                "test_taker_id": userData.id,
                "notes":userNotes
            }
        }).catch((error) => {
            return;
        });
    };


    useEffect(() => {
        if (!userData) return;
        runTopScores();
        runAllExams();

    }, [userData])
    
    useEffect(() => {
        if (!allExams) return;
        setGridRows(allExams.data);
    }, [allExams])

    useEffect (() => {
        if (!topScores) return;
        setMaxRows(topScores.data);
    }, [topScores])

    useEffect(() => {
        if (user_data) {
            getUsers(user_data, setUserData);
        }
    }, [user_data])

    const [userDataRefreshTrigger, setUserDataRefreshTrigger] = useState(false);
    useEffect(() => {
        if (userData) {
            getUsers(user_data, setUserData);
        }
    }, [userDataRefreshTrigger]);

    
    const [scheduleReservations, setScheduleReservations] = useState(null);
    const [scheduleRows, setScheduleRows] = useState([]);
    useEffect(() => {
        if (!userData) {
            return
        }
        getReservedTimeSlotsForUsers([userData.id], setScheduleReservations)
    }, [userData])

    useEffect(() => {
        if (!userData || !scheduleReservations) {
            return
        }
        if (scheduleReservations) {
            setScheduleRows(scheduleReservations['data'][userData.id.toString()]);
        }
    },[scheduleReservations, userData]);

    const scheduleGridColumns = [
        { flex: 0.5, headerName: 'ID', field: 'id'},
        { flex: 1, headerName: 'Date', field: 'exam_date', renderCell: (params) => <span>{formatTimeZoneDateString(params.row.exam_date)}</span> },
        { flex: 1, headerName: 'Start Time', field: 'start_time', renderCell: (params) => <span>{formatTime(params.row.start_time)}</span> },
        { flex: 1, headerName: 'End Time', field: 'end_time', renderCell: (params) => <span>{formatTime(params.row.end_time)}</span> },
        { flex: 1, headerName: 'Components', field: 'component_type', renderCell: (params) => <span>{params.row.component_type}</span> },
        { flex: 1, headerName: 'Notes', field: 'notes', renderCell: (params) => <span>{params.row.notes}</span> },
    ];

    const paginationModel = { page: 0, pageSize: 5 };

    const gridColumns = [
        { headerName: 'ID', field: 'id'},
        { flex: 1.5, headerName: 'Exam', field: 'exam_component_base__exam_base__exam_title'},
        { flex: 0.5, headerName: 'Component', field: 'exam_component_base__exam_component_title' },
        { flex: 0.5, headerName: 'Date Taken (YYYY-MM-DD)', field:'date_taken', renderCell: (params) => <span>{formatTimeZoneDateString(params.row.date_taken)}</span>},
        { flex: 0.5, headerName: 'Score', field: 'score'},
        {
            field: 'view',
            headerName: '',
            width:60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        onRowClick(params);
                    }}
                    aria-label="view user"
                >
                    <VisibilityIcon />
                </IconButton>
            )
        }
    ];

    const maxScoreColumns = [
        { headerName: 'ID', field: 'id'},
        { flex: 1.5, headerName: 'Exam', field: 'exam_component_base__exam_base__exam_title'},
        { flex: 0.5, headerName: 'Component', field: 'exam_component_base__exam_component_title' },
        { flex: 0.5, headerName: 'Max Score', field: 'max_score'},
    ];

    const [tokenRequestOpen, setTokenRequestOpen] = useState(false);
    const toggleTokenRequestOpen = () => setTokenRequestOpen(!tokenRequestOpen);

    const [liveAnswersOpen, setLiveAnswersOpen] = useState(false);
    const handleOpenLiveAnswers = () => {
        setLiveAnswersOpen(true);
    };
    const handleCloseLiveAnswers = () => {
        setLiveAnswersOpen(false);
        setSelectedExamComponentId(null);
    };

    const [selectedExamComponentId, setSelectedExamComponentId] = useState(null);

    const onRowClick = (params) => {
        const examComponentId = params.row.id;
        if (!examComponentId) return;
        setSelectedExamComponentId(examComponentId);
    };

    useEffect(() => {
        if (!selectedExamComponentId) return;
        handleOpenLiveAnswers();
    }, [selectedExamComponentId])


    const handleWritingOptionClick = (option) => {
        saveUserInfo({'writing_passed':option, 'id': userData.id}, alert, alert);
        sendWritingScoreReportEmail(option, 'IASA', userData.id);
        setUserDataRefreshTrigger(!userDataRefreshTrigger);
    };

    const handleClericalWritingOptionClick = (option) => {
        saveUserInfo({'clerical_writing_passed':option, 'id': userData.id}, alert, alert);
        sendWritingScoreReportEmail(option, 'Clerical', userData.id);
        setUserDataRefreshTrigger(!userDataRefreshTrigger);
    };


    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // STYLES 
    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    const outerBoxStyle = {
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 3,
        borderRadius: 4,
        width:'100%',
        height:'auto',
    };

    const outerBoxStyleMobile = {
        display: 'flex',
        flexDirection: 'column',
        p:2,
        width: '100vw',
        height:'auto',

    };

    const headerStyle = {
        bgcolor: '#69B0FC',
        color:'#FFFFFF',
        px:2,
        pt:2,
        borderTopRightRadius:16,
        borderTopLeftRadius:16,
        pb:1,
        borderBottomLeftRadius: !userData ? 16 : 0,
        borderBottomRightRadius: !userData ? 16 : 0,

    }

    const headerTextStyle = {fontWeight:650, textShadow: '2px 2px 4px rgba(0, 0, 0, 0.25)'}

    const gridStyle = {m:0, borderRadius:4, cursor:'pointer'}

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '95vw',
        minWidth:'75vw',
        maxHeight: '95vh',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: 24,
        display:'flex',
        p:1,
        flexDirection:'column',
        [`.${gridClasses.cell}.fail`]: {
          backgroundColor: '#FF9494',
          color: '#000000',
        },
        [`.${gridClasses.cell}.pass`]: {
          backgroundColor: '#A3FFAE',
          color: '#000000',
        },
        [`.${gridClasses.cell}.null`]: {
          backgroundColor: '#EBEBEB',
          color: '#000000',
        },
      }




    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // HTML COMPONENT
    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    return (
        <Box sx={modalView?modalStyle:{}}>
            <Box sx={outerBoxStyle}>
                <Modal
                    open={tokenRequestOpen}
                    onClose={toggleTokenRequestOpen}
                >
                    <ExamTokenRequestModal data={userData}  handleClose={toggleTokenRequestOpen} refreshHook={() => setUserDataRefreshTrigger(!userDataRefreshTrigger)}/>
                </Modal>
                <Modal
                    open={liveAnswersOpen}
                    onClose={handleCloseLiveAnswers}
                >
                    <LiveAnswersModal examComponentId={selectedExamComponentId} />
                </Modal>
                <Box sx={headerStyle}>
                    <Stack direction={'row'} sx={{display:'flex', justifyContent:'space-between', width:'100%'}}>
                        <Typography variant='h4' sx={headerTextStyle}>
                            {!userData ? 'Select User' : formatName(userData.last_name) + ', ' + formatName(userData.first_name)}
                        </Typography>
                        {userData && (
                            <div className='token-count'><Paid /> {userData.exam_tokens}</div>
                        )}
                    </Stack>
                    {userData && (
                        <Stack direction={'row'} gap={1}>
                            <Typography variant='p'>{userData.email}</Typography>
                            <Typography variant='p'>|</Typography>
                            <Typography variant='p'>{formatPhoneNumber(userData.phone_number)}</Typography>
                        </Stack>
                    )}

                </Box>
                { userData && (
                    <Stack 
                        spacing={1} 
                        sx={{
                            p:2,
                            [`.${gridClasses.cell}.fail`]: {
                                backgroundColor: '#FF9494',
                                color: '#000000',
                            },
                            [`.${gridClasses.cell}.pass`]: {
                                backgroundColor: '#A3FFAE',
                                color: '#000000',
                            },
                            [`.${gridClasses.cell}.null`]: {
                                backgroundColor: '#EBEBEB',
                                color: '#000000',
                            },
                        }}>
                        <Stack direction={"row"} spacing={2} sx={{display:'flex'}}>
                            <Stack spacing={1} sx={{flex:0.60}}>
                                <Typography variant='h6'>Top Scores</Typography>
                                <DataGrid
                                    sx={gridStyle}
                                    rows={maxRows}
                                    columns={maxScoreColumns}
                                    initialState={{ 
                                    pagination: { paginationModel },
                                    columns: {
                                        columnVisibilityModel: {
                                        id: false,
                                        }
                                    }
                                    }}
                                    getCellClassName={(params) => {
                                    if (!['max_score'].includes(params.field)) {
                                        return '';
                                    };
                                    if (params.value === null || params.value === undefined) {
                                        return 'null';  // Ensure null or undefined values are caught first
                                    };
                                    return params.value >= 80.0 ? 'pass' : 'fail';
                                    }}
                                />
                            </Stack>
                            <Stack spacing={1} sx={{flex:0.40}}>
                                <Stack direction={'row'}>
                                    <Typography variant='h6' sx={{textAlign:'left', mr:1}}>{userData ? "IA/SA Attempts Taken: " : ""}</Typography>
                                    <Typography variant='h6' sx={{textAlign:'left', color:userData.attempts_taken >= 3 ? 'red' : 'black'}}>{userData.attempts_taken}</Typography>
                                </Stack>
                                <Typography variant='h6'>IA/SA Writing Qualification</Typography>
                                <Stack direction={'row'} sx={{width:'100%', justifyContent:'space-between', border: 'solid', p:1, borderWidth:1, borderRadius:1, borderColor:'#e0e0e0'}}>
                                    <Typography onClick={() => handleWritingOptionClick('true')} sx={{bgcolor: userData.writing_passed === 'true' ? theme.success_light : theme.background, borderRadius:4, textAlign:'left', pr:2, pl:2, cursor:'pointer'}}>Pass</Typography>
                                    <Typography onClick={() => handleWritingOptionClick('no grade')} sx={{bgcolor: userData.writing_passed === 'no grade' || !userData.writing_passed ? theme.background_grey : theme.background, borderRadius:4, textAlign:'left', pr:2, pl:2, cursor:'pointer'}}>No Grade</Typography>
                                    <Typography onClick={() => handleWritingOptionClick('false')} sx={{bgcolor: userData.writing_passed === 'false' ? theme.error_light : theme.background, borderRadius:4, textAlign:'left', pr:2, pl:2, cursor:'pointer'}}>Fail</Typography>
                                </Stack>
                                <Typography variant='h6'>Clerical Writing Qualification</Typography>
                                <Stack direction={'row'} sx={{width:'100%', justifyContent:'space-between', border: 'solid', p:1, borderWidth:1, borderRadius:1, borderColor:'#e0e0e0'}}>
                                    <Typography onClick={() => handleClericalWritingOptionClick('true')} sx={{bgcolor: userData.clerical_writing_passed === 'true' ? theme.success_light : theme.background, borderRadius:4, textAlign:'left', pr:2, pl:2, cursor:'pointer'}}>Pass</Typography>
                                    <Typography onClick={() => handleClericalWritingOptionClick('no grade')} sx={{bgcolor: userData.clerical_writing_passed === 'no grade' || !userData.writing_passed ? theme.background_grey : theme.background, borderRadius:4, textAlign:'left', pr:2, pl:2, cursor:'pointer'}}>No Grade</Typography>
                                    <Typography onClick={() => handleClericalWritingOptionClick('false')} sx={{bgcolor: userData.clerical_writing_passed === 'false' ? theme.error_light : theme.background, borderRadius:4, textAlign:'left', pr:2, pl:2, cursor:'pointer'}}>Fail</Typography>
                                </Stack>
                                <Typography variant='h6'>Profile Notes</Typography>
                                <TextField
                                    multiline
                                    maxRows={10}
                                    rows={10}
                                    value={userNotes}
                                    onChange={(e) => {
                                        setUserNotes(e.target.value);
                                    }}
                                />
                                <Button variant={'contained'} onClick={handleUpdateNotes}>Save Notes</Button>
                                <Typography variant='h6'>Profile Operations</Typography>
                                <Button variant={'contained'} onClick={() => toggleTokenRequestOpen()}>Add Exam Tokens +</Button>
                            </Stack>
                            
                        </Stack>
                        <Stack>
                            <Typography variant='h6'>All Attempted Exams</Typography>
                            <DataGrid
                                sx={gridStyle}
                                rows={gridRows}
                                columns={gridColumns}
                                initialState={{ 
                                pagination: { paginationModel },
                                columns: {
                                    columnVisibilityModel: {
                                    id: false,
                                    }
                                }
                                }}
                                pageSizeOptions={[5, 10, 20, 50]}
                                getCellClassName={(params) => {
                                if (!['score'].includes(params.field)) {
                                    return '';
                                };
                                if (params.value === null || params.value === undefined) {
                                    return 'null';  // Ensure null or undefined values are caught first
                                };
                                return params.value >= 80.0 ? 'pass' : 'fail';
                                }}
                            />
                            {scheduleReservations && (
                                <Box sx={{ borderTop: '1px solid #e0e0e0'}}>
                                    <Typography sx={{textAlign:'left', mt:1, mb:1}} variant="h6">
                                        Scheduled Reservations
                                    </Typography>
                                    <DataGrid 
                                        sx={gridStyle}
                                        rows={scheduleRows}
                                        columns={scheduleGridColumns}
                                        initialState={{ 
                                            pagination: { paginationModel },
                                            columns: {
                                                columnVisibilityModel: {
                                                    id: false,
                                                }
                                            },
                                        }}
                                        disableRowSelectionOnClick
                                        pageSizeOptions={[10, 30, 50, 100]}
                                    />  
                                </Box>
                            )}
                        </Stack>
                    </Stack>
                )}
            </Box>
        </Box>
    )
}

export default SingleUserTile;